import type { MetaFunction } from "@remix-run/node";
import Footer from "~/components/Footer";
import Header from "~/components/Header";

export const meta: MetaFunction = () => {
  return [
    { title: "Privacy Policy - Cloud Exam Hub" },
    {
      name: "description",
      content:
        "Privacy Policy for Cloud Exam Hub - Learn about how we handle and protect your personal information.",
    },
  ];
};

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow pt-20">
        <div className="max-w-4xl mx-auto px-4 py-8 font-sans">
          <h1 className="text-3xl font-bold text-gray-900 border-b-2 border-gray-200 pb-4 mb-8">
            Privacy Policy for Cloud Exam Hub
          </h1>

          <p className="text-gray-600 italic mb-8">
            Last Updated: January 3, 2025
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              1. Introduction
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Cloud Exam Hub is a service offering of Square Zero Labs LLC
              (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;). We
              provide this Privacy Policy to explain our practices regarding
              personal information. By using our services, you expressly consent
              to the collection, use, disclosure, and retention of your
              information as described in this Privacy Policy. If you do not
              agree with these terms, please do not use our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              2. Information We Collect
            </h2>
            <div className="space-y-4">
              <h3 className="text-xl font-medium text-gray-800">
                2.1 Personal Information
              </h3>
              <p className="text-gray-700">We may collect:</p>
              <ul className="list-disc pl-5 space-y-2 text-gray-700">
                <li>Account information (name, email, credentials)</li>
                <li>Payment and billing information</li>
                <li>Test performance and progress data</li>
                <li>Communication preferences and history</li>
                <li>Customer service interactions</li>
                <li>Any other information you choose to provide</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mt-6">
                2.2 Automatically Collected Information
              </h3>
              <ul className="list-disc pl-5 space-y-2 text-gray-700">
                <li>Device and network information</li>
                <li>Usage data and analytics</li>
                <li>Login and activity timestamps</li>
                <li>Technical data about your interactions</li>
                <li>Cookies and similar technologies</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              3. How We Use Your Information
            </h2>
            <p className="text-gray-700 mb-4">
              We may use your information for:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-gray-700">
              <li>Providing and maintaining our services</li>
              <li>Processing transactions and payments</li>
              <li>Monitoring and analyzing usage patterns</li>
              <li>Improving our services</li>
              <li>Communicating with you about our services</li>
              <li>Preventing fraud and abuse</li>
              <li>Complying with legal obligations</li>
              <li>Any other purpose with your consent</li>
            </ul>
            <p className="text-gray-700 mt-4">
              We reserve the right to use aggregated, anonymized, or
              de-identified data without restriction.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              4. California Privacy Rights
            </h2>
            <div className="space-y-4 text-gray-700">
              <p>
                Under the California Consumer Privacy Act (CCPA), California
                residents have certain rights regarding their personal
                information:
              </p>

              <h3 className="text-xl font-medium text-gray-800">
                4.1 Your Rights
              </h3>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Right to know what personal information we collect and how it
                  is used
                </li>
                <li>
                  Right to delete your personal information, subject to
                  exceptions
                </li>
                <li>Right to opt-out of the sale of personal information</li>
                <li>Right to non-discrimination for exercising your rights</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800">
                4.2 Limitations and Exceptions
              </h3>
              <p>
                These rights are subject to certain limitations and exceptions,
                including but not limited to:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Legal obligations requiring data retention</li>
                <li>Technical limitations</li>
                <li>Security and fraud prevention requirements</li>
                <li>Impact on other users&apos; rights</li>
                <li>Business operational requirements</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800">
                4.3 Request Process
              </h3>
              <p>To submit a request:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Email: privacy@cloudexamhub.com</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              5. Information Sharing and Disclosure
            </h2>
            <div className="space-y-4 text-gray-700">
              <p>
                We do not sell personal information. We may share information
                with:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Service providers and contractors</li>
                <li>Payment processors</li>
                <li>Analytics providers</li>
                <li>Legal authorities when required</li>
                <li>Business partners</li>
                <li>Successor entities in a merger or acquisition</li>
              </ul>
              <p>
                We reserve the right to share aggregated or de-identified data
                without restriction.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              6. Data Security
            </h2>
            <div className="space-y-4 text-gray-700">
              <p>
                We implement reasonable security measures but cannot guarantee
                absolute security. We are not responsible for unauthorized
                access beyond our reasonable control.
              </p>
              <p>You are responsible for:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Maintaining password confidentiality</li>
                <li>Reporting unauthorized account access</li>
                <li>Using secure networks</li>
                <li>Logging out of active sessions</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              7. Data Retention
            </h2>
            <p className="text-gray-700 leading-relaxed">
              We retain information as long as necessary for legitimate business
              purposes or legal requirements. We may retain data indefinitely in
              aggregated or anonymized form. We reserve the right to delete data
              at our discretion.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              8. Cookies and Tracking
            </h2>
            <div className="space-y-4 text-gray-700">
              <p>By using our services, you consent to our use of:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Essential cookies and similar technologies for service
                  operation
                </li>
                <li>Analytics and performance cookies</li>
                <li>Functionality cookies</li>
                <li>Advertising and targeting cookies</li>
              </ul>
              <p>
                Disabling cookies may limit or prevent access to certain
                features.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              9. Children&apos;s Privacy
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Our services are not directed to children under 18. We do not
              knowingly collect information from children under 18. By using the
              services, you represent that you are at least 18 or that you are
              the parent or guardian of such a minor and consent to such minor
              using the services. If we learn we have collected information from
              a child under 18, we will take reasonable measures to promptly
              delete such information unless legally required to retain it.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              10. Changes to This Policy
            </h2>
            <p className="text-gray-700 leading-relaxed">
              We may modify this Privacy Policy at any time without prior
              notice. Changes are effective immediately upon posting. Your
              continued use of our services after changes constitutes
              acceptance. We recommend checking this policy regularly.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              11. Limitation of Liability
            </h2>
            <p className="text-gray-700 leading-relaxed">
              To the maximum extent permitted by law, we are not liable for any
              damages arising from the use, disclosure, or security of your
              information. Your sole remedy for any privacy-related claims is to
              stop using our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              12. Governing Law
            </h2>
            <p className="text-gray-700 leading-relaxed">
              This Privacy Policy is governed by California law. Any disputes
              will be resolved exclusively in the state or federal courts
              located in California.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              13. Data Requests
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Based on the applicable laws of your jurisdiction, you may have
              the right to request access to the personal information we collect
              from you, details about how we have processed it, correct
              inaccuracies or delete your personal information. You may also
              have the right to withdraw your consent to our processing of your
              personal information.
            </p>
            <p className="text-gray-700 leading-relaxed">
              These rights may be limited in some circumstances by applicable
              law. To make a request regarding your personal information, please
              contact us at: privacy@cloudexamhub.com.
            </p>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
